import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { failureOf } from '@/api/errors';
import {
  useConversation,
  useConversations,
  useOpenConversation,
  usePeople,
  useSaveDraft,
  useSendMessage,
  useThreads,
  type WireConversationMessage,
} from '@/api/hooks';
import { useMeOrThrow } from '@/app/MeContext';
import { useDepartmentLabel } from '@/app/departments';
import { useFrame } from '@/app/frame';
import { Avatar, Badge, Button, Kicker, SendButton } from '@/ui/primitives';
import { Refusal } from '@/ui/Refusal';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';
import { viewerOf } from '@/directory/explainKit';
import { explainColleague } from './explain';

/**
 * The Team tab (P:1304-1325, ADR-0089): everyone else, and a conversation
 * with one of them. A conversation is its two members' and nobody else's;
 * a draft the twin prepared is shown to its person only, and only the
 * person sends it. What the two of you were both on in the department is
 * listed as topics, by thread, from what you can already open in Mail.
 */
export function TeamPane({ talkWith, onTalkWith }: { talkWith: string | null; onTalkWith: (humanId: string | null) => void }) {
  const me = useMeOrThrow();
  const people = usePeople();
  const conversations = useConversations(true);
  const label = useDepartmentLabel();
  const opener = useOpenConversation();
  const who = useWho();
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Picking a person finds or makes the one conversation the two of you have.
  useEffect(() => {
    setConversationId(null);
    if (!talkWith) return;
    const known = conversations.data?.conversations.find((c) => c.with.humanId === talkWith);
    if (known) {
      setConversationId(known.id);
      return;
    }
    opener.mutate(talkWith, { onSuccess: (r) => setConversationId(r.conversationId) });
    // Only a change of person reopens; the list refetching does not.
  }, [talkWith]);

  const others = (people.data?.people ?? []).filter((p) => p.id !== me.humanId && p.state === 'here');
  const lastWith = new Map((conversations.data?.conversations ?? []).map((c) => [c.with.humanId, c.lastMessageAt]));
  const newFrom = new Set((conversations.data?.conversations ?? []).filter((c) => c.unread).map((c) => c.with.humanId));

  if (talkWith) {
    const person = others.find((p) => p.id === talkWith) ?? (people.data?.people ?? []).find((p) => p.id === talkWith);
    return (
      <Conversation
        conversationId={conversationId}
        name={person?.displayName ?? 'A colleague'}
        opening={opener.isPending}
        openError={opener.error}
        onBack={() => onTalkWith(null)}
      />
    );
  }

  return (
    <ol className="m-0 flex flex-1 list-none flex-col gap-px overflow-y-auto bg-line p-0" aria-label="Team">
      {people.isPending && <li className="bg-raised p-4 text-dense text-meta">Reading who is here…</li>}
      {people.isError && (
        <li className="bg-raised p-4">
          <Refusal compact failure={failureOf(people.error)} />
        </li>
      )}
      {people.data && others.length === 0 && <li className="bg-raised p-4 text-dense text-meta">Nobody else is here yet.</li>}
      {[...others]
        .sort((a, b) => {
          const la = lastWith.get(a.id) ?? '';
          const lb = lastWith.get(b.id) ?? '';
          if (la !== lb) return lb.localeCompare(la);
          return a.displayName.localeCompare(b.displayName);
        })
        .map((p) => (
          <li key={p.id} className="bg-raised">
            <button type="button" onClick={() => onTalkWith(p.id)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-sunken">
              <Avatar name={p.displayName} size={28} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="flex items-center gap-2 truncate text-dense text-ink">
                  {p.displayName}
                  {newFrom.has(p.id) && (
                    <span data-testid="conversation-new">
                      <Badge tone="accent">NEW</Badge>
                    </span>
                  )}
                </span>
                <span className="truncate text-label text-meta">
                  {p.departments.map((d) => label(d)).join(', ') || 'No department'}
                  {lastWith.get(p.id) ? ` · last ${new Date(lastWith.get(p.id)!).toLocaleDateString('en-GB')}` : ''}
                </span>
              </span>
            </button>
            <div className="px-4 pb-2">
              <Explainer context={explainColleague(p, { lastAt: lastWith.get(p.id) ?? null, unread: newFrom.has(p.id), onTalk: () => onTalkWith(p.id) }, who, viewerOf(me))} />
            </div>
          </li>
        ))}
    </ol>
  );
}

function Conversation({
  conversationId,
  name,
  opening,
  openError,
  onBack,
}: {
  conversationId: string | null;
  name: string;
  opening: boolean;
  openError: unknown;
  onBack: () => void;
}) {
  const me = useMeOrThrow();
  const frame = useFrame();
  const conversation = useConversation(conversationId);
  const threads = useThreads(me.department);
  const send = useSendMessage(conversationId);
  const save = useSaveDraft(conversationId);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const first = name.split(' ')[0] ?? name;
  const subjects = new Map((threads.data?.threads ?? []).map((t) => [t.id, t.subject ?? 'A conversation']));
  const messages = conversation.data?.messages ?? [];
  const topics = conversation.data?.topics ?? [];

  const submit = () => {
    const words = text.trim();
    if (!words || !conversationId) return;
    send.mutate(
      { text: words, ...(editing ? { draftId: editing } : {}) },
      {
        onSuccess: () => {
          setText('');
          setEditing(null);
        },
      },
    );
  };

  return (
    <>
      <div className="flex items-center gap-2 border-b border-line bg-page px-3 py-2">
        <Button size="row" variant="quiet" onClick={onBack}>
          ← Team
        </Button>
        <span className="truncate text-dense font-semibold" data-testid="talking-with">{name}</span>
      </div>
      <ol className="m-0 flex flex-1 list-none flex-col gap-3 overflow-y-auto p-4" aria-label={`Conversation with ${name}`} aria-live="polite">
        {topics.length > 0 && (
          <li className="flex flex-col gap-1.5 border border-line bg-page px-3 py-2.5" data-testid="topics">
            <Kicker>What you were both on</Kicker>
            {topics.map((t) => (
              <Link key={t.threadId} to={`/mail/${t.threadId}`} className="text-dense text-ink no-underline hover:underline">
                {subjects.get(t.threadId) ?? 'A conversation in Mail'}
              </Link>
            ))}
          </li>
        )}
        {(opening || conversation.isPending) && conversationId === null && !openError && (
          <li className="text-dense text-meta">Opening your conversation with {first}…</li>
        )}
        {Boolean(openError) && (
          <li>
            <Refusal compact failure={failureOf(openError)} />
          </li>
        )}
        {conversation.data && messages.filter((m) => !m.sent).length === 0 && (
          <li className="text-dense leading-[1.6] text-secondary">
            Nothing said yet. What you write here is seen by {first} and nobody else, and stays on the record.
          </li>
        )}
        {messages
          .filter((m) => !m.sent)
          .map((m) => (
            <Message
              key={m.id}
              message={m}
              mine={m.authorHuman === me.humanId}
              name={first}
              twin={frame.twin}
              onSend={() => send.mutate({ text: m.body ?? '', draftId: m.id })}
              onEdit={() => {
                setText(m.body ?? '');
                setEditing(m.id);
              }}
              sending={send.isPending}
            />
          ))}
        {send.isError && (
          <li>
            <Refusal compact failure={failureOf(send.error)} />
          </li>
        )}
        {save.isError && (
          <li>
            <Refusal compact failure={failureOf(save.error)} />
          </li>
        )}
      </ol>
      <form
        className="flex gap-2 border-t border-line bg-page p-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          aria-label={`Message ${first}`}
          className="min-h-tap min-w-0 flex-1 border border-control bg-raised px-3 text-dense text-ink placeholder:text-meta"
          placeholder={editing ? 'Edit the draft, then send it…' : `Message ${first}…`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={!conversationId}
        />
        <SendButton ready={!!text.trim()} disabled={!text.trim() || !conversationId || send.isPending} />
        {/* Kept, not sent: it waits above with Send and Edit beside it. */}
        {!editing && (
          <Button
            size="row"
            variant="quiet"
            disabled={!text.trim() || !conversationId || save.isPending}
            onClick={() => save.mutate(text.trim(), { onSuccess: () => setText('') })}
          >
            Save as a draft
          </Button>
        )}
      </form>
    </>
  );
}

function Message({
  message,
  mine,
  name,
  twin,
  onSend,
  onEdit,
  sending,
}: {
  message: WireConversationMessage;
  mine: boolean;
  name: string;
  twin: string | null;
  onSend: () => void;
  onEdit: () => void;
  sending: boolean;
}) {
  if (message.state === 'draft') {
    return (
      <li className="flex flex-col items-end gap-1.5" data-testid="draft-message">
        <div className="flex max-w-[88%] flex-col gap-2 border border-dashed border-control bg-page px-3.5 py-2.5">
          <span className="text-micro uppercase tracking-label text-secondary">
            Draft{message.writtenByAgent ? ` · ${twin ?? 'your twin'} prepared it` : ''} · only you see it
          </span>
          <p className="m-0 whitespace-pre-wrap text-dense">{message.body ?? 'The draft is no longer readable.'}</p>
          <span className="flex gap-2">
            <Button icon="forward" size="row" variant="primary" disabled={sending || !message.body} onClick={onSend}>Send it</Button>
            <Button icon="edit" size="row" variant="quiet" onClick={onEdit}>Edit</Button>
          </span>
        </div>
      </li>
    );
  }
  const flagged = message.scanVerdict !== 'clean';
  return (
    <li className={`flex flex-col gap-1 ${mine ? 'items-end' : 'items-start'}`}>
      {!mine && <span className="text-label font-semibold uppercase tracking-label text-secondary">{name}</span>}
      <p
        className={`m-0 max-w-[88%] whitespace-pre-wrap px-3.5 py-2.5 text-dense ${
          mine ? 'bg-[var(--btn-bg)] text-[var(--btn-fg)]' : 'border border-line bg-page text-ink'
        }`}
      >
        {message.body ?? 'No longer readable.'}
      </p>
      {flagged && (
        <span className="flex items-center gap-1.5 text-label text-secondary">
          <span aria-hidden className={`block h-2 w-2 ${message.scanVerdict === 'blocked' ? 'bg-danger' : 'bg-warning'}`} />
          scan: {message.scanVerdict}
        </span>
      )}
    </li>
  );
}
